import Stack from '@mui/material/Stack';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { UnitMeasurement } from 'api';
import { UnitApi } from 'api/UnitApi';
import { unitKeys } from 'api/queries/UnitQueries';
import { Unit } from 'api/responses/models/Unit/UnitStructure/Unit';
import { UpdateUnitInfoRequest } from 'api/responses/models/Unit/UpdateUnitInfoRequest';
import { PageButtons } from 'library/components/controls/buttons/PageButtons';
import { PageFormContainer } from 'library/components/controls/pageTemplates/PageFormContainer';
import { usePrompt } from 'library/hooks/useRouterNavigation';
import { useValidationErrorsSnackbar } from 'library/hooks/useValidationErrorsSnackbar';
import { useCheckConsoleDebuggingReadAccess } from 'main/auth/checkUserAccess';
import { isUnitMarine } from 'modules/Shared/State/unit';
import { mapUnitDtoToUnitDataFormTypes } from 'modules/UnitCreation/State';
import { BrandModelSection } from 'modules/UnitCreation/WizardSteps/UnitInformationStep/Sections/BrandModelSection';
import { DimensionsSection } from 'modules/UnitCreation/WizardSteps/UnitInformationStep/Sections/DimensionsSection';
import { IdentifiersIndustrialSection } from 'modules/UnitCreation/WizardSteps/UnitInformationStep/Sections/IdentifiersIndustrialSection';
import { IdentifiersMarineSection } from 'modules/UnitCreation/WizardSteps/UnitInformationStep/Sections/IdentifiersMarineSection';
import { ManufacturedBySection } from 'modules/UnitCreation/WizardSteps/UnitInformationStep/Sections/ManufacturedBySection';

import mapUnitInfoFormToRequest from '../Mappers';
import { UnitGeneralSection } from './UnitGeneralSection';

const unitApi = new UnitApi();

export const EditUnitInformation = ({
	unitData,
	unitMeasurement,
	onBack,
}: {
	unitData: Unit;
	unitMeasurement: UnitMeasurement | null | undefined;
	onBack: () => void;
}): React.JSX.Element => {
	const { t } = useTranslation(['unitCreation', 'common', 'commonButton']);
	const { enqueueSnackbar } = useSnackbar();
	const { enqueueValidationSnackbar } = useValidationErrorsSnackbar();
	const { checkConsoleDebuggingReadAccess } =
		useCheckConsoleDebuggingReadAccess();
	const { mainSegment } = unitData;

	const isMarine = isUnitMarine(mainSegment);

	const queryClient = useQueryClient();

	const unitInfoMutation = useMutation<
		boolean,
		unknown,
		UpdateUnitInfoRequest
	>({
		mutationFn: async (data) => {
			return (await unitApi.updateUnitInformation(data)).data;
		},
		onSuccess: async (_data, variables) => {
			// Invalidate and refetch
			if (variables.unitId) {
				await queryClient.invalidateQueries({
					queryKey: unitKeys.single(variables.unitId),
				});
			}

			onBack();
		},
	});

	const methods = useForm({
		defaultValues: mapUnitDtoToUnitDataFormTypes(unitData, unitMeasurement),
	});

	const {
		handleSubmit,
		formState: { isDirty },
		watch,
	} = methods;

	const Prompt = usePrompt(
		t('common:unsaved-warning-dialog-message'),
		isDirty
	);

	const onSubmit = handleSubmit(
		async (data) => {
			try {
				await unitInfoMutation.mutateAsync(
					mapUnitInfoFormToRequest(data, unitData.id)
				);
			} catch (error) {
				checkConsoleDebuggingReadAccess() && console.error(error);

				enqueueValidationSnackbar(error as Error);
			}
		},
		(err) => {
			checkConsoleDebuggingReadAccess() && console.error(err);

			enqueueSnackbar(t('common:validation-error-message'), {
				variant: 'error',
			});
		}
	);

	return (
		<PageFormContainer noValidate onSubmit={onSubmit}>
			<Stack
				direction="column"
				sx={{
					gap: 2,

					'.MuiInputBase-root.MuiOutlinedInput-root': {
						backgroundColor: 'background.paper',
					},
				}}>
				<FormProvider {...methods}>
					<UnitGeneralSection
						consumerLineOfBusiness={watch('lineOfBusiness')}
						mainSegment={mainSegment}
					/>

					{isMarine ? (
						<IdentifiersMarineSection />
					) : (
						<IdentifiersIndustrialSection />
					)}
					<BrandModelSection mainSegment={mainSegment} />
					{isMarine && <DimensionsSection />}
					<ManufacturedBySection
						manufacturerName={unitData.manufacturerName ?? null}
					/>
				</FormProvider>
			</Stack>
			<PageButtons
				primaryButtonLabel={t('commonButton:save')}
				secondaryButtonLabel={t('commonButton:back')}
				secondaryButtonAction={onBack}
				primaryButtonDisabled={!isDirty}
				isPrimaryButtonSubmitType
			/>
			{Prompt}
		</PageFormContainer>
	);
};
